import { Space, Typography } from 'antd';
import { FormikProvider, useFormik, validateYupSchema, yupToFormErrors } from 'formik';
import { get, merge } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import PackageIcon from 'components/icons/PackageIcon';
import Button from 'components/new-ui/base/button/Button';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { AISMessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import IrelandImportH1DeclarationUtils from 'views/declarations/ireland/import/h1/utils';
import { getIrelandH1ItemColumns } from '../ireland/irelandH1ItemColumns';
import validate, {
    FormModel,
    ValidationSchema,
    transformErrorsForFormik,
} from 'views/declarations/uk/export/validations/validations';
import { renderMissingValue } from '../utils';
import { ItemErrors } from '../../types';
import { withSeededIrelandH1Packaging, withSeededUkPackaging } from '../../utils';
import DiscardChangesModal from './DiscardChangesModal';
import ItemDetails from './ItemDetails';
import { getColumnErrorPaths, getItemFieldsRegistry } from './itemFieldsRegistry';

type Props = {
    open: boolean;
    onClose: () => void;
    onPrevItem: () => void;
    onNextItem: () => void;
    onSave: (rawItem: any) => void | Promise<void>;
    item: any;
    itemNumber: number;
    totalItems: number;
    // The item's *current* errors - drives the "Completed" state of the item being viewed.
    itemErrors: ItemErrors | undefined;
    // The item's errors as of when the page loaded. Deciding which fields to render from these
    // (rather than from the live ones) keeps the fields on screen after a successful save, so a
    // wrong value entered in the quick fix can be corrected without reloading the page.
    initialItemErrors: ItemErrors | undefined;
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    messageType: MessageTypes;
    sourceDeclarationUrl?: string;
};

const isIrelandH1 = (country: DeclarationCountry, internalType: DeclarationInternalType, messageType: MessageTypes) =>
    country === DeclarationCountry.IRELAND &&
    internalType === DeclarationInternalType.IMPORT &&
    messageType === AISMessageTypes.H1;

// Packaging is normally an editable list, but the "fix missing field" view only ever edits the
// first entry - seed one so the packaging fields below always have somewhere to bind to.
const withSeededPackaging = (item: any, irelandH1: boolean) =>
    irelandH1 ? withSeededIrelandH1Packaging(item) : withSeededUkPackaging(item);

const FixItemModal = ({
    open,
    onClose,
    onPrevItem,
    onNextItem,
    onSave,
    item,
    itemNumber,
    totalItems,
    itemErrors,
    initialItemErrors,
    country,
    internalType,
    messageType,
    sourceDeclarationUrl,
}: Props) => {
    const irelandH1 = isIrelandH1(country, internalType, messageType);
    const itemNumberDisplay = itemNumber + 1;
    const title = `Fix Missing Fields - Item ${itemNumberDisplay} of ${totalItems}`;

    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Values the user entered but that couldn't be saved yet (the item is still invalid), kept
    // per item index so moving between items doesn't throw the input away. Cleared per item on a
    // successful save, and wholesale when the modal closes.
    const draftsRef = useRef<Record<number, any>>({});

    const irelandProductColumns = useMemo(() => getIrelandH1ItemColumns(renderMissingValue), []);
    // For now works only with ireland products columns
    const productColumns = irelandProductColumns;

    const itemFieldsRegistry = useMemo(
        () => getItemFieldsRegistry(country, internalType, messageType),
        [country, internalType, messageType]
    );

    const columnErrorPaths = useMemo(
        () => getColumnErrorPaths(country, internalType, messageType),
        [country, internalType, messageType]
    );

    // A field is "missing" when the item's validation errors (the same ones driving the red cells
    // in the errors table) had an error at one of the column's known paths when the page loaded.
    const missingFieldKeys = useMemo(
        () =>
            Object.keys(itemFieldsRegistry).filter((key) =>
                columnErrorPaths[key]?.some((path) => get(initialItemErrors, path))
            ),
        [itemFieldsRegistry, columnErrorPaths, initialItemErrors]
    );

    const combinedValidationSchema = useMemo(() => {
        const schemas = missingFieldKeys.map((key) => itemFieldsRegistry[key]!.validationSchema);
        if (schemas.length > 0 && Yup.isSchema(schemas[0])) {
            return schemas.reduce(
                (acc: Yup.AnyObjectSchema, schema) => acc.concat(schema as Yup.AnyObjectSchema),
                Yup.object()
            );
        }
        return merge({}, ...schemas) as ValidationSchema;
    }, [missingFieldKeys, itemFieldsRegistry]);

    // A cached draft wins over the item itself, so returning to a half-filled item restores what
    // the user typed rather than the (still unsaved) server values.
    const initialValues = useMemo(
        () => draftsRef.current[itemNumber] ?? withSeededPackaging(item, irelandH1),
        [item, itemNumber, irelandH1]
    );

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validateOnMount: true,
        validate: async (values) => {
            if (Yup.isSchema(combinedValidationSchema)) {
                try {
                    await validateYupSchema(values, combinedValidationSchema);
                    return {};
                } catch (yupError) {
                    return yupToFormErrors(yupError);
                }
            }
            return transformErrorsForFormik(
                await validate(new FormModel(values), combinedValidationSchema as ValidationSchema)
            );
        },
        onSubmit: () => undefined,
    });

    const { dirty, isValid, values } = formik;

    useEffect(() => {
        if (!open) draftsRef.current = {};
    }, [open]);

    const saveCurrentItem = useCallback(async () => {
        const rawItem = irelandH1 ? IrelandImportH1DeclarationUtils.transformProductForServer(values) : values;
        setIsSaving(true);
        try {
            await onSave(rawItem);
            delete draftsRef.current[itemNumber];
            return true;
        } catch {
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [irelandH1, values, onSave, itemNumber]);

    // Saves first when there's something valid to save, so progress is never lost by navigating.
    // Input that isn't valid yet can't be persisted, so it's cached in memory instead.
    const goToItem = useCallback(
        async (navigate: () => void) => {
            if (dirty) {
                if (isValid) {
                    const saved = await saveCurrentItem();
                    if (!saved) return;
                } else {
                    draftsRef.current[itemNumber] = values;
                }
            }
            navigate();
        },
        [dirty, isValid, values, itemNumber, saveCurrentItem]
    );

    const itemDetailsUrl = useMemo(
        () => (sourceDeclarationUrl && item?.id ? `${sourceDeclarationUrl}/products/${item.id}` : undefined),
        [sourceDeclarationUrl, item?.id]
    );

    const hasPrev = itemNumber > 0;
    const hasNext = itemNumber < totalItems - 1;
    const isFixed = !!itemErrors && Object.keys(itemErrors).length === 0;

    const handleRequestClose = () => {
        const hasUnsavedInput = (dirty && !isFixed) || Object.keys(draftsRef.current).length > 0;
        if (!hasUnsavedInput) {
            onClose();
            return;
        }

        setIsDiscardModalOpen(true);
    };

    const handleDiscard = () => {
        setIsDiscardModalOpen(false);
        onClose();
    };

    // Saves in place and keeps the modal open - moving between items is left to the Prev/Next
    // buttons (which save on their own), so the primary action never navigates unexpectedly.
    // With nothing left to save it turns into a Close, which goes through the same unsaved-input
    // guard as the X - drafts for *other* items can still be pending even when this one is clean.
    const handleConfirm = () => {
        if (!dirty) {
            handleRequestClose();
            return;
        }
        saveCurrentItem();
    };

    const confirmButtonText = dirty ? 'Save' : 'Close';

    return (
        <>
            <ConfirmationModal
                open={open}
                onClose={handleRequestClose}
                onCancel={handleRequestClose}
                onConfirm={handleConfirm}
                icon={<PackageIcon />}
                title={title}
                width={900}
                confirmButtonText={confirmButtonText}
                confirmationButtonDisabled={dirty && !isValid}
                confirmationButtonLoading={isSaving}
                content={
                    <Space
                        size={24}
                        direction="vertical"
                        style={{ width: '100%', paddingInline: 16, paddingBlock: 12 }}
                    >
                        <Space size={12} direction="vertical" style={{ width: '100%' }}>
                            <Space size={8}>
                                <Typography>
                                    {isFixed
                                        ? 'All required details for this Item have been filled in. You can still update them below.'
                                        : 'This Item is missing details required for generation. Please fill the fields below.'}
                                </Typography>
                            </Space>
                            <ItemDetails
                                itemNumber={itemNumberDisplay}
                                record={item}
                                columns={productColumns}
                                fixed={isFixed}
                            />
                        </Space>

                        {missingFieldKeys.length > 0 && (
                            <FormikProvider value={formik}>
                                <Space
                                    size={12}
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                    className="fixed-row-layout"
                                >
                                    {missingFieldKeys.map((key) => {
                                        const { Component, props } = itemFieldsRegistry[key]!;
                                        return <Component key={key} {...props} />;
                                    })}
                                </Space>
                            </FormikProvider>
                        )}

                        {itemDetailsUrl && (
                            <div>
                                <div style={{ textAlign: 'right' }}>
                                    <Link to={itemDetailsUrl}>See Item Details</Link>
                                </div>
                            </div>
                        )}
                    </Space>
                }
                footerLeft={
                    <div>
                        <Space size={8}>
                            <Button
                                type="link"
                                style={{ color: 'var(--brand-6)' }}
                                disabled={!hasPrev || isSaving}
                                onClick={() => goToItem(onPrevItem)}
                            >
                                <LeftOutlined style={{ color: 'inherit' }} /> Go to Prev Item
                            </Button>
                            <Button
                                type="link"
                                style={{ color: 'var(--brand-6)' }}
                                disabled={!hasNext || isSaving}
                                onClick={() => goToItem(onNextItem)}
                            >
                                Go to Next Item <RightOutlined style={{ color: 'inherit' }} />
                            </Button>
                        </Space>
                    </div>
                }
            />
            <DiscardChangesModal
                open={isDiscardModalOpen}
                onDiscard={handleDiscard}
                onCancel={() => setIsDiscardModalOpen(false)}
            />
        </>
    );
};

export default FixItemModal;
